// frontend/admin-portal/src/contexts/SettingsContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { settingsService } from "../services/settingsService";
import { useAuth } from "./AuthContext";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const list = await settingsService.list();
      const map = {};
      for (const s of list) map[s.key] = s.value;
      setSettings(map);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const get = (key, fallback = null) =>
    settings[key] !== undefined ? settings[key] : fallback;

  const refresh = () => load();

  const update = async (key, value) => {
    await settingsService.update(key, value);
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const updateMany = async (patch) => {
    await settingsService.updateMany(patch);
    setSettings((prev) => ({ ...prev, ...patch }));
  };

  return (
    <SettingsContext.Provider
      value={{ settings, loading, error, get, refresh, update, updateMany }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
};
